/**
 * The monthly-PDF review flow, client side.
 *
 * Extraction no longer writes anything a client can see. A batch is created, the
 * files are uploaded (fast), then read one request at a time (each one is a slow
 * LLM job), the result is reviewed and corrected, and only פרסום turns it into
 * client data.
 *
 * The reading is driven by whoever has the screen open, but the QUEUE lives on
 * the server — so a reload does not lose the upload, it rejoins it.
 */
import { api } from '../../api.js';

export interface BatchWarning {
  code: string;
  level: 'error' | 'warn' | 'info';
  message: string;
}

export interface BatchFile {
  file_id: string;
  filename: string;
  fund: string;
  status: 'pending' | 'extracting' | 'extracted' | 'error';
  report_date: string | null;
  total_value: number | null;
  positions_count: number;
  deposits: number | null;
  withdrawals: number | null;
  flows_from_pdf: boolean;
  flows_confirmed: boolean;
  fund_confirmed: boolean;
  error: string | null;
  warnings: BatchWarning[];
}

export interface ClientFund {
  fund: string;
  last_date: string | null;
  last_total: number | null;
}

export interface Batch {
  batch_id: string;
  client_id: string;
  status: 'draft' | 'ready' | 'published' | 'discarded' | 'rolled_back';
  created_at: string;
  files: BatchFile[];
  /** The client's existing funds — the dropdown's options. */
  client_funds: ClientFund[];
  warnings: BatchWarning[];
  can_publish: boolean;
}

export interface PublishResult {
  batch_id: string;
  published: { fund: string; report_date: string; total_value: number; positions: number; return_computed: boolean }[];
}

/** An upload that was read but never published — still open in review. */
export interface OpenBatch {
  batch_id: string;
  client_id: string;
  created_at: string;
  created_by: string;
  files: number;
  extracted: number;
  /** Files still mid-read — the upload may be live in another window. */
  extracting: number;
}

export interface FileEdits {
  fund?: string;
  deposits?: number | null;
  withdrawals?: number | null;
  flowsConfirmed?: boolean;
  fundConfirmed?: boolean;
}

/** How far the reading has got. Counted and timed by the server, so it is the
 *  same in every window and a reload picks the clock back up. */
export interface BatchProgress {
  batch_id: string;
  client_id: string;
  status: Batch['status'];
  total: number;
  extracted: number;
  failed: number;
  pending: number;
  extracting: number;
  reading: { filename: string; elapsed_seconds: number } | null;
}

export interface ExtractStep extends BatchProgress {
  /** The file that request read, or null when there was nothing free to read. */
  read: string | null;
}

export const pdfBatch = {
  create: (clientId: string) =>
    api.post<{ batch_id: string }>('/admin/ingest/batch', { clientId }).then((r) => r.batch_id),

  /** Upload the bytes. Seconds, not minutes — the reading happens below. */
  addFile: (batchId: string, file: File) => {
    const form = new FormData();
    form.append('pdf', file);
    return api.upload<BatchProgress>(`/admin/ingest/batch/${batchId}/file`, form);
  },

  /** Read one more file. One request per file: each is minutes long. */
  extractNext: (batchId: string) =>
    api.post<ExtractStep>(`/admin/ingest/batch/${batchId}/extract-next`, {}),

  progress: (batchId: string) => api.get<BatchProgress>(`/admin/ingest/batch/${batchId}/progress`),

  get: (batchId: string) => api.get<Batch>(`/admin/ingest/batch/${batchId}`),
  /** Drafts left open — an upload nobody published is otherwise invisible. */
  listOpen: (clientId?: string) =>
    api.get<OpenBatch[]>(`/admin/ingest/batches${clientId ? `?clientId=${encodeURIComponent(clientId)}` : ''}`),
  edit: (batchId: string, fileId: string, edits: FileEdits) =>
    api.patch<Batch>(`/admin/ingest/batch/${batchId}/file/${fileId}`, edits),
  removeFile: (batchId: string, fileId: string) =>
    api.del<Batch>(`/admin/ingest/batch/${batchId}/file/${fileId}`),
  publish: (batchId: string) => api.post<PublishResult>(`/admin/ingest/batch/${batchId}/publish`, {}),
  discard: (batchId: string) => api.post<{ ok: true }>(`/admin/ingest/batch/${batchId}/discard`, {}),
  rollback: (batchId: string) => api.post<{ funds: string[] }>(`/admin/ingest/batch/${batchId}/rollback`, {}),
};

export interface Snapshot {
  holding_id: string;
  fund: string;
  report_date: string | null;
  total_value: number;
  is_latest: boolean;
  source: string;
  positions: number;
  has_return: boolean;
  /** Is there an earlier return to measure this month against? */
  has_prev_return: boolean;
  ingest_batch_id: string | null;
}

export const snapshots = {
  list: (clientId: string) => api.get<Snapshot[]>(`/admin/clients/${clientId}/snapshots`),
  remove: (holdingId: string) => api.del<{ ok: true }>(`/admin/snapshots/${encodeURIComponent(holdingId)}`),
};
